import { useQuery } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { getHeaders } from '../api/apiUtils';
/** Normalizes the absent-key and explicit-null spellings of "no organization". */
var asOrganization = function (organizationId) { return organizationId !== null && organizationId !== void 0 ? organizationId : null; };
var AUTHORIZATION_CONTEXT_PATH = 'czna-users/users/me/authorization-context';
var STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes
var GLOBAL_ROLE_AUTHORITY_ADMIN = 'AUTHORITY_ADMIN';
/**
 * Who the signed-in user is — their id, their library, its sigla, and whether they are a
 * global administrator.
 *
 * <p><b>CZNA-598 — this hook no longer decides what anyone may DO.</b> It used to carry
 * five hand-written predicates (`canEditProposal`, `canReviewProposal`,
 * `canForwardProposal`, `canApproveNationally`, `canCreateProposalOfType`), each
 * re-implementing a subset of the backend's rules from the raw permission rows. Nothing
 * forced the two implementations to move together, and they did not: CZNA-591, CZNA-594,
 * CZNA-596, CZNA-601 and CZNA-602 are five separate defects of that one shape, all filed
 * inside a single week, and at one point this very file compared organizations in one
 * predicate and ignored them in another eight lines below.
 *
 * <p>Permission questions are now answered by the backend and read, never recomputed:
 * <ul>
 *   <li>on an existing proposal — {@code proposalAllows(proposal, 'edit' | 'submit' | …)}
 *       over `ProposalSummary.actions`;</li>
 *   <li>on creating a new one — {@code useAllowedProposalActions()}.</li>
 * </ul>
 *
 * <p>Adding a predicate here again would recreate the split. If an answer is missing, it
 * is missing from `ProposalActionResolver` on the backend, where adding it reaches the
 * screen and the refusal at the same moment.
 */
var useAuthorizationContext = function () {
    var _a, _b;
    var ky = useOkapiKy();
    var _c = useQuery(['authorization-context'], 
    // CZNA-598 (blind review) — the module's standard no-store headers, as on every
    // `api/*Service.ts` GET. Who this user is and what they hold is a PER-CALLER answer;
    // an intermediary serving it to the next user is the failure that matters here.
    function () { return ky.get(AUTHORIZATION_CONTEXT_PATH, { headers: getHeaders() })
        .json(); }, 
    // CZNA-321: cacheTime too, not only staleTime — the module default is 0, which drops
    // the answer the moment the last form unmounts. Every propose-flow step is a new form
    // mount, so the next step found the query gone, fell back to loading and remounted,
    // losing the parent's one-shot restore. See ProposalForm.flowStepCache.czna-321.test.
    { staleTime: STALE_TIME_MS, cacheTime: STALE_TIME_MS }), data = _c.data, isLoading = _c.isLoading, isError = _c.isError;
    var isAdmin = (_b = (_a = data === null || data === void 0 ? void 0 : data.globalRoles) === null || _a === void 0 ? void 0 : _a.includes(GLOBAL_ROLE_AUTHORITY_ADMIN)) !== null && _b !== void 0 ? _b : false;
    /** See {@link AuthorizationHelpers.isFromOriginatingInstitution} — wording, not a gate. */
    var isFromOriginatingInstitution = function (proposalOrganizationId) {
        var proposalOrg = asOrganization(proposalOrganizationId);
        if (proposalOrg === null)
            return true;
        return proposalOrg === asOrganization(data === null || data === void 0 ? void 0 : data.organizationId);
    };
    return {
        context: data,
        isLoading: isLoading,
        isError: isError,
        isAdmin: isAdmin,
        isFromOriginatingInstitution: isFromOriginatingInstitution,
    };
};
export default useAuthorizationContext;
